import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import { useNotification } from '@/components/notifications';
import { isSafeHttpUrl } from '@/utils/url-utils';

const POLL_MS = 2000;
const TIMEOUT_MS = 3 * 60 * 1000;
// Grace after the popup closes, since the success page closes itself and its
// callback may still be in flight.
const CLOSE_GRACE_MS = 5000;

export type PollOutcome = 'connected' | 'abandoned' | 'timed-out' | 'waiting';

/**
 * What one poll tick concluded. `settled` has to win over `closed`, because the
 * success page closes the popup itself and both become true at the same moment.
 */
export const pollOutcome = ({
  settled,
  closedForMs,
  elapsedMs,
}: {
  settled: boolean;
  closedForMs: number | null;
  elapsedMs: number;
}): PollOutcome => {
  if (settled) return 'connected';
  if (closedForMs !== null && closedForMs > CLOSE_GRACE_MS) return 'abandoned';
  if (elapsedMs > TIMEOUT_MS) return 'timed-out';
  return 'waiting';
};

export type ConsentAttempt = {
  redirectUrl: string;
  isSettled: () => Promise<boolean>;
};

export type ConsentHandlers = {
  onConnected: () => void;
  onAbandoned?: () => void;
  onTimedOut: () => void;
};

/**
 * Runs an OAuth consent in a popup and polls until it settles.
 *
 * The popup is opened synchronously in the click, before `begin` awaits the
 * API for the consent URL, so it keeps the click's user activation and isn't
 * popup-blocked. Its final page is served from the API origin and cannot talk
 * back, so completion is detected by polling `isSettled`. Rejects with
 * whatever `begin` throws; resolves false when the browser blocked the popup.
 */
export const useOAuthConsentPopup = (windowPrefix: string) => {
  const notification = useNotification();
  const [waiting, setWaiting] = useState<Set<string>>(new Set());
  const timers = useRef<number[]>([]);

  useEffect(
    () => () => {
      timers.current.forEach(window.clearInterval);
      timers.current = [];
    },
    [],
  );

  const setKeyWaiting = useCallback(
    (key: string, isWaiting: boolean) =>
      setWaiting((prev) => {
        const next = new Set(prev);
        if (isWaiting) next.add(key);
        else next.delete(key);
        return next;
      }),
    [],
  );

  const start = useCallback(
    async (
      key: string,
      begin: () => Promise<ConsentAttempt>,
      handlers: ConsentHandlers,
    ): Promise<boolean> => {
      const popup = window.open(
        '',
        `${windowPrefix}-${key}`,
        'width=520,height=680',
      );
      if (!popup) {
        notification.error(
          'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.',
        );
        return false;
      }
      // Not the `noopener` feature: that makes window.open() return null and
      // would always read as blocked above.
      try {
        popup.opener = null;
      } catch {
        // Some browsers reject the write; consent proceeds either way.
      }

      setKeyWaiting(key, true);
      let attempt: ConsentAttempt;
      try {
        attempt = await begin();
        if (!isSafeHttpUrl(attempt.redirectUrl)) {
          throw new Error('Received an unsafe authorization URL');
        }
      } catch (error) {
        popup.close();
        setKeyWaiting(key, false);
        throw error;
      }
      popup.location.href = attempt.redirectUrl;

      const startedAt = performance.now();
      let closedAt: number | null = null;
      // isSettled can outlast POLL_MS. Without this, overlapping ticks each
      // see a settled grant and each report success.
      let inFlight = false;
      let finished = false;

      const poll = window.setInterval(async () => {
        if (inFlight || finished) return;
        inFlight = true;
        if (closedAt === null && popup.closed) closedAt = performance.now();

        let settled = false;
        try {
          settled = await attempt.isSettled();
        } catch (error) {
          // A transient read failure should not abandon a consent in flight.
          Sentry.captureException(error, { extra: { windowPrefix, key } });
        } finally {
          inFlight = false;
        }

        const outcome = pollOutcome({
          settled,
          closedForMs: closedAt === null ? null : performance.now() - closedAt,
          elapsedMs: performance.now() - startedAt,
        });
        if (outcome === 'waiting') return;

        finished = true;
        window.clearInterval(poll);
        timers.current = timers.current.filter((t) => t !== poll);
        setKeyWaiting(key, false);
        if (outcome === 'connected') handlers.onConnected();
        else if (outcome === 'timed-out') handlers.onTimedOut();
        else handlers.onAbandoned?.();
      }, POLL_MS);
      timers.current.push(poll);
      return true;
    },
    [notification, setKeyWaiting, windowPrefix],
  );

  const isWaiting = useCallback((key: string) => waiting.has(key), [waiting]);

  return { start, isWaiting };
};
